import { useState, useEffect, Fragment } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { searchOutreach, getOutreachDetail, getOutreachFormAttributes } from '@services/api';
import { useDebounce } from '@hooks/useDebounce';
import { usePersistentState } from '@hooks/usePersistentState';
import { formatDateTime } from '@utils/format';
import { parseApiError } from '@utils/errorUtils';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import RecordingPlayer from '@components/common/RecordingPlayer';
import './OutreachSearchTab.css';

// The four user-facing outreach channels, derived server-side from (sType, sCategory).
// Keep in sync with OutreachTypes in OutreachSearchController.cs.
const OUTREACH_TYPES = ['Phone', 'Email', 'Text', 'Push'];
const DEFAULT_OUTREACH_TYPE = 'Phone';

// Debounce for the free-text filters so we don't fire a search on every keystroke.
const SEARCH_DEBOUNCE_MS = 350;

// Formats a Date for a datetime-local input using LOCAL wall-clock time.
// Returns '' for null/invalid dates so clearing the field can't crash the render.
const toDateTimeLocalValue = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

export default function OutreachSearchTab() {
  const { data: formAttributes } = useQuery({
    queryKey: ['outreachFormAttributes'],
    queryFn: getOutreachFormAttributes,
    staleTime: Infinity,
    retry: 1,
  });
  const outreachTypes = formAttributes?.outreachTypes ?? OUTREACH_TYPES;
  const callCategories = formAttributes?.categories ?? [];
  const dispositionReasons = formAttributes?.dispositionReasons ?? [];
  const companies = formAttributes?.companies ?? [];
  const directions = formAttributes?.directions ?? [];

  // Pagination + filter state persist across tab switches (the tab unmounts when
  // you navigate away) so returning to Outreach Search restores the last search.
  const [page, setPage] = usePersistentState('outreach.page', 1);
  const [pageSize, setPageSize] = usePersistentState('outreach.pageSize', 25);
  const [showFilters, setShowFilters] = useState(true);
  const [twilioAccountSid, setTwilioAccountSid] = useState(null);

  // Expanded row state
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [callDetail, setCallDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [filters, setFilters] = usePersistentState('outreach.filters', () => ({
    startDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
    endDate: new Date(),
    outreachType: DEFAULT_OUTREACH_TYPE,
    phoneNumber: '',
    callSid: '',
    patientName: '',
    coachName: '',
    category: '',
    dispositionReason: '',
    companyId: '',
    direction: '',
  }));

  useEffect(() => {
    try {
      const authToken = sessionStorage.getItem('authToken');
      if (authToken) {
        const payload = authToken.split('.')[1];
        if (payload) {
          const decoded = JSON.parse(atob(payload));
          if (decoded.twilio_account_sid) {
            setTwilioAccountSid(decoded.twilio_account_sid);
          }
        }
      }
    } catch (err) {
      console.debug('Could not extract account SID from token:', err);
    }
  }, []);

  // Debounce the filter object so typing in the free-text filters doesn't fire a
  // search per keystroke; the debounced value feeds the query key below.
  const debouncedFilters = useDebounce(filters, SEARCH_DEBOUNCE_MS);

  // React Query gives us result caching across tab switches, built-in out-of-order
  // response handling, and (via placeholderData) keeps the previous results on
  // screen while the next query loads so the table never blanks on a filter change.
  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['outreachSearch', debouncedFilters, page, pageSize],
    queryFn: () => searchOutreach({ ...debouncedFilters, page, pageSize }),
    placeholderData: keepPreviousData,
  });

  const results = data?.results ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalCount = data?.totalCount ?? 0;

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
      endDate: new Date(),
      outreachType: DEFAULT_OUTREACH_TYPE,
      phoneNumber: '',
      callSid: '',
      patientName: '',
      coachName: '',
      category: '',
      dispositionReason: '',
      companyId: '',
      direction: '',
    });
    setPage(1);
  };

  const handleRowClick = async (contactId) => {
    if (selectedCallId === contactId) {
      setSelectedCallId(null);
      setCallDetail(null);
      return;
    }

    setSelectedCallId(contactId);
    setCallDetail(null);
    setDetailLoading(true);

    try {
      const detail = await getOutreachDetail(contactId);
      setCallDetail(detail);
    } catch (err) {
      console.error('Error fetching outreach detail:', err);
      setCallDetail({ error: 'Failed to load outreach detail' });
    } finally {
      setDetailLoading(false);
    }
  };

  const getTwilioConsoleUrl = (callSid) => {
    if (!callSid) return null;
    if (!/^CA[0-9a-fA-F]{32}$/.test(callSid)) return null;

    const frameUrl = `/console/voice/calls/logs/${callSid}`;
    const base = new URL('https://console.twilio.com/us1/monitor/logs/calls');
    base.searchParams.set('frameUrl', frameUrl);
    base.searchParams.set('x-target-region', 'us1');
    base.searchParams.set('__override_layout__', 'embed');
    base.searchParams.set('bifrost', 'true');

    if (twilioAccountSid) {
      base.searchParams.set('accountSid', twilioAccountSid);
    }
    return base.toString();
  };

  const CallSidLink = ({ sid }) => {
    if (!sid) return null;
    return (
      <a
        href={getTwilioConsoleUrl(sid)}
        target="_blank"
        rel="noopener noreferrer"
        className="call-sid-link"
        title="View in Twilio Console"
        onClick={(e) => e.stopPropagation()}
      >
        {sid}
      </a>
    );
  };

  // Colors reference the shared design tokens in styles/colors.css.
  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'Outgoing Call': return 'var(--color-category-outgoing)';
      case 'Incoming Call': return 'var(--color-category-incoming)';
      case 'Left Voicemail': return 'var(--color-category-voicemail)';
      case 'IVR Resupply': return 'var(--color-category-ivr)';
      default: return 'var(--color-neutral)';
    }
  };

  const getOutreachTypeColor = (type) => {
    switch (type) {
      case 'Phone': return 'var(--color-type-phone)';
      case 'Email': return 'var(--color-type-email)';
      case 'Text': return 'var(--color-type-text)';
      case 'Push': return 'var(--color-type-push)';
      default: return 'var(--color-neutral)';
    }
  };

  return (
    <div className="call-search-tab">
      <div className="call-search-header">
        <h2>Outreach Search</h2>
        <button
          className="filter-toggle-button"
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Outreach Type</label>
            <select
              value={filters.outreachType}
              onChange={(e) => handleFilterChange('outreachType', e.target.value)}
            >
              <option value="">All Types</option>
              {outreachTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              value={toDateTimeLocalValue(filters.startDate)}
              onChange={(e) => handleFilterChange('startDate', e.target.value ? new Date(e.target.value) : null)}
            />
          </div>
          <div className="filter-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              value={toDateTimeLocalValue(filters.endDate)}
              onChange={(e) => handleFilterChange('endDate', e.target.value ? new Date(e.target.value) : null)}
            />
          </div>
          <div className="filter-group">
            <label>Phone Number</label>
            <input
              type="text"
              placeholder="Search by phone number"
              value={filters.phoneNumber}
              onChange={(e) => handleFilterChange('phoneNumber', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Call SID</label>
            <input
              type="text"
              placeholder="Twilio Call SID"
              value={filters.callSid}
              onChange={(e) => handleFilterChange('callSid', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Patient Name</label>
            <input
              type="text"
              placeholder="Search by patient name"
              value={filters.patientName}
              onChange={(e) => handleFilterChange('patientName', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Coach Name</label>
            <input
              type="text"
              placeholder="Search by coach name"
              value={filters.coachName}
              onChange={(e) => handleFilterChange('coachName', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">All Categories</option>
              {callCategories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Disposition</label>
            <select
              value={filters.dispositionReason}
              onChange={(e) => handleFilterChange('dispositionReason', e.target.value)}
            >
              <option value="">All Dispositions</option>
              {dispositionReasons.map((d) => (
                <option key={d.id} value={d.reason}>{d.reason}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Company</label>
            <select
              value={filters.companyId}
              onChange={(e) => handleFilterChange('companyId', e.target.value)}
            >
              <option value="">All Companies</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Direction</label>
            <select
              value={filters.direction}
              onChange={(e) => handleFilterChange('direction', e.target.value)}
            >
              <option value="">All Directions</option>
              {directions.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <button className="clear-filters-button" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
      )}

      <div className="call-search-container">
        {isError && (
          <div className="search-error-message">
            Failed to search outreach: {parseApiError(error)}
          </div>
        )}

        {isLoading ? (
          <Loading message="Loading outreach..." />
        ) : results.length === 0 ? (
          <EmptyState message="No outreach found for the selected filters" />
        ) : (
          <>
            {isFetching && <div className="results-updating" aria-live="polite">Updating…</div>}
            <div className="call-results-table">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Type</th>
                    <th>Direction</th>
                    <th>Category</th>
                    <th>Patient</th>
                    <th>Coach</th>
                    <th>Company</th>
                    <th>To / From</th>
                    <th>Answered</th>
                    <th>Disposition</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((call) => (
                    <Fragment key={call.contactId}>
                      <tr
                        className={`call-row ${selectedCallId === call.contactId ? 'expanded' : ''}`}
                        onClick={() => handleRowClick(call.contactId)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRowClick(call.contactId);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-expanded={selectedCallId === call.contactId}
                      >
                        <td className="call-timestamp">
                          {formatDateTime(call.timestamp)}
                          {call.hasRecording && (
                            <span className="recording-indicator" role="img" aria-label="Recording available">🎧</span>
                          )}
                        </td>
                        <td>
                          <span
                            className="outreach-type-badge"
                            style={{ backgroundColor: getOutreachTypeColor(call.outreachType) }}
                          >
                            {call.outreachType || '-'}
                          </span>
                        </td>
                        <td>{call.direction || '-'}</td>
                        <td>
                          <span
                            className="category-badge"
                            style={{ backgroundColor: getCategoryColor(call.category) }}
                          >
                            {call.category || '-'}
                          </span>
                        </td>
                        <td className="call-person">{call.patientName || '-'}</td>
                        <td className="call-person">{call.coachName || '-'}</td>
                        <td className="call-company">{call.companyName || '-'}</td>
                        <td className="call-phone">
                          {call.origination && call.destination
                            ? `${call.origination} → ${call.destination}`
                            : call.origination || call.destination || '-'}
                        </td>
                        <td>
                          {call.answered === true && <span className="answered-badge yes">Yes</span>}
                          {call.answered === false && <span className="answered-badge no">No</span>}
                          {call.answered == null && '-'}
                        </td>
                        <td className="call-disposition">{call.dispositionReason || '-'}</td>
                      </tr>

                      {selectedCallId === call.contactId && (
                        <tr className="detail-row">
                          <td colSpan="10">
                            {detailLoading && (
                              <div className="detail-loading">Loading detail...</div>
                            )}
                            {callDetail?.error && (
                              <div className="detail-error">{callDetail.error}</div>
                            )}
                            {callDetail && !callDetail.error && !detailLoading && (
                              <OutreachDetailPanel detail={callDetail} CallSidLink={CallSidLink} formatTimestamp={formatDateTime} />
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <div className="page-info">
                Page {page} of {totalPages} ({totalCount} total records)
              </div>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="page-size-select"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function OutreachDetailPanel({ detail, CallSidLink, formatTimestamp }) {
  const timingPairs = [
    ['Client Leg', detail.clientLegStartTime, detail.clientLegEndTime],
    ['Outside Party Leg', detail.outsidePartyLegStartTime, detail.outsidePartyLegEndTime],
    ['Queue', detail.queueStartTime, detail.queueEndTime],
    ['Conference', detail.conferenceStartTime, detail.conferenceEndTime],
    ['Connection', detail.connectionStartTime, detail.connectionEndTime],
  ].filter(([, start, end]) => start || end);

  return (
    <div className="call-detail">
      <div className="detail-grid">
        <div className="detail-section">
          <h4>Call IDs</h4>
          {detail.callSid && (
            <p><strong>Call SID:</strong> <CallSidLink sid={detail.callSid} /></p>
          )}
          {detail.parentCallSid && (
            <p><strong>Parent Call SID:</strong> <CallSidLink sid={detail.parentCallSid} /></p>
          )}
          <p><strong>Contact ID:</strong> {detail.contactId}</p>
          {detail.contactGuid && (
            <p><strong>Contact GUID:</strong> <span className="guid-text">{detail.contactGuid}</span></p>
          )}
        </div>

        <div className="detail-section">
          <h4>People</h4>
          <p><strong>Patient:</strong> {detail.patientName || 'N/A'}</p>
          <p><strong>Coach:</strong> {detail.coachName || 'N/A'}</p>
          <p><strong>Company:</strong> {detail.companyName || 'N/A'}</p>
        </div>

        <div className="detail-section">
          <h4>Call Info</h4>
          <p><strong>Type:</strong> {detail.outreachType || '-'}</p>
          <p><strong>Direction:</strong> {detail.direction || '-'}</p>
          <p><strong>Category:</strong> {detail.category || '-'}</p>
          <p><strong>Answered:</strong> {detail.answered === true ? 'Yes' : detail.answered === false ? 'No' : '-'}</p>
          <p><strong>Success:</strong> {detail.success ? 'Yes' : 'No'}</p>
          {detail.isIvr && <p><strong>IVR Call:</strong> Yes</p>}
          {detail.isAutomation && <p><strong>Automated:</strong> Yes</p>}
          {detail.timesInQueue > 0 && <p><strong>Times in Queue:</strong> {detail.timesInQueue}</p>}
        </div>

        <div className="detail-section">
          <h4>Disposition</h4>
          <p><strong>Reason:</strong> {detail.dispositionReason || 'N/A'}</p>
          {detail.transferReason && <p><strong>Transfer Reason:</strong> {detail.transferReason}</p>}
          {detail.dispositionNotes && <p><strong>Notes:</strong> {detail.dispositionNotes}</p>}
        </div>
      </div>

      {timingPairs.length > 0 && (
        <div className="detail-section timing-section">
          <h4>Timing</h4>
          <div className="timing-grid">
            {timingPairs.map(([label, start, end]) => (
              <div key={label} className="timing-pair">
                <span className="timing-label">{label}:</span>
                <span className="timing-value">
                  {formatTimestamp(start)} {end ? `→ ${formatTimestamp(end)}` : '(ongoing)'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.notes && (
        <div className="detail-section">
          <h4>Notes</h4>
          <pre className="notes-text">{detail.notes}</pre>
        </div>
      )}

      {detail.recordingSid && (
        <div className="detail-section">
          <h4>Recording</h4>
          <RecordingPlayer contactId={detail.contactId} recordingSid={detail.recordingSid} />
        </div>
      )}

      {detail.twilioEnrichment && (
        <div className="detail-section twilio-section">
          <h4>Twilio Data</h4>
          <p><strong>Status:</strong> {detail.twilioEnrichment.status || '-'}</p>
          {detail.twilioEnrichment.price && (
            <p><strong>Price:</strong> ${detail.twilioEnrichment.price}</p>
          )}
          {detail.twilioEnrichment.duration != null && (
            <p><strong>Duration:</strong> {Math.floor(detail.twilioEnrichment.duration / 60)}m {detail.twilioEnrichment.duration % 60}s</p>
          )}
        </div>
      )}
    </div>
  );
}
